/**
 * Shared Managed Dropdown Component
 *
 * Provides consistent "Manage" dropdown across sections with:
 * - Freeze/Activate actions
 * - Delete action
 * - Optional status change actions (Seeds)
 * - Optional scan profile assignment (Assets/Seeds)
 *
 * Uses the legacy Dropdown + Menu pattern consistent with all other action
 * dropdowns in the codebase. Submenu support for scan profiles is provided
 * via the `menu` prop with nested `submenu` arrays.
 *
 * @example
 * ```tsx
 * // Assets usage
 * <ManagedDropdown
 *   onFreezeClick={handlers.handleFreeze}
 *   onActivateClick={handlers.handleActivate}
 *   onDeleteClick={handlers.handleDelete}
 *   shortcuts={{ freeze: '⌥ F', activate: '⌥ U', delete: '⌥ D' }}
 * />
 *
 * // Seeds usage with status options and scan profiles
 * <ManagedDropdown
 *   onFreezeClick={handlers.handleFreeze}
 *   onActivateClick={handlers.handleActivate}
 *   onDeleteClick={handlers.handleDelete}
 *   onStatusChangeClick={(status) => handlers.handleStatusChange(status)}
 *   statusOptions={[
 *     { label: 'Approve', value: 'accept', icon: <CheckCircle />, shortcut: '⌥ A' },
 *     { label: 'Reject', value: 'reject', icon: <XCircle />, shortcut: '⌥ R' }
 *   ]}
 *   profileOptions={[{ label: 'Default (Global)', value: '' }, { label: 'Fast', value: 'fast-id' }]}
 *   onProfileChangeClick={(id) => updateProfile(id)}
 *   currentProfileId="fast-id"
 * />
 * ```
 *
 * @module components/actions
 */

import { CheckIcon } from '@heroicons/react/24/outline';
import { EllipsisHorizontalCircleIcon } from '@heroicons/react/24/solid';
import { useMemo } from 'react';

import { ACTION_ICONS } from '@/components/actions/constants/actionIcons';
import type { ManagedDropdownProps } from '@/components/actions/types';
import { Dropdown } from '@/components/Dropdown';
import { MenuItemProps } from '@/components/Menu';
import { cn } from '@/utils/classname';

/**
 * Default labels for managed actions
 */
const DEFAULT_LABELS = {
  freeze: 'Freeze',
  activate: 'Activate',
  delete: 'Delete',
} as const;

/**
 * Managed Dropdown Component
 *
 * Renders a dropdown with Freeze, Activate, Delete, and optionally Status Change
 * and Scan Profile actions. Uses the legacy Dropdown + Menu pattern for sub-menu
 * support (scan profiles).
 */
export function ManagedDropdown({
  onFreezeClick,
  onActivateClick,
  onDeleteClick,
  onStatusChangeClick,
  statusOptions,
  onProfileChangeClick,
  profileOptions,
  currentProfileId,
  labels,
  shortcuts,
  hideActions,
  label = 'Manage',
  disabled = false,
  className,
  'data-testid': testId = 'managed-dropdown',
}: ManagedDropdownProps) {
  const activeProfileId = currentProfileId || '';

  const menuItems = useMemo<MenuItemProps[]>(() => {
    const items: MenuItemProps[] = [];

    // Status change actions (Seeds: Approve/Reject)
    const hasStatusOptions =
      !hideActions?.statusChange &&
      statusOptions &&
      statusOptions.length > 0 &&
      onStatusChangeClick;

    if (hasStatusOptions) {
      statusOptions.forEach(statusOption => {
        items.push({
          label: statusOption.label,
          icon: statusOption.icon,
          count: statusOption.shortcut,
          onClick: () => onStatusChangeClick(statusOption.value),
        });
      });
      items.push({ label: '', type: 'divider' });
    }

    // Scan Profile sub-menu
    const hasProfileOptions =
      profileOptions && profileOptions.length > 0 && onProfileChangeClick;

    if (hasProfileOptions) {
      const profileSubmenuItems: MenuItemProps[] = profileOptions.map(opt => ({
        label: opt.label,
        icon:
          opt.value === activeProfileId ? (
            <CheckIcon className="size-4" />
          ) : (
            <span className="size-4" />
          ),
        onClick: () => onProfileChangeClick(opt.value),
      }));

      items.push({
        label: 'Change Scan Profile',
        icon: <ACTION_ICONS.STATUS_CHANGE className="size-4" />,
        submenu: profileSubmenuItems,
      });
    }

    // Freeze action
    if (!hideActions?.freeze) {
      items.push({
        label: labels?.freeze ?? DEFAULT_LABELS.freeze,
        icon: <ACTION_ICONS.FREEZE className="size-4" />,
        count: shortcuts?.freeze,
        onClick: onFreezeClick,
      });
    }

    // Activate action
    if (!hideActions?.activate) {
      items.push({
        label: labels?.activate ?? DEFAULT_LABELS.activate,
        icon: <ACTION_ICONS.ACTIVATE className="size-4" />,
        count: shortcuts?.activate,
        onClick: onActivateClick,
      });
    }

    // Divider before delete
    if (!hideActions?.delete) {
      items.push({ label: '', type: 'divider' });
    }

    // Delete action
    if (!hideActions?.delete) {
      items.push({
        label: labels?.delete ?? DEFAULT_LABELS.delete,
        icon: <ACTION_ICONS.DELETE className="size-4" />,
        count: shortcuts?.delete,
        onClick: onDeleteClick,
      });
    }

    return items;
  }, [
    statusOptions,
    hideActions,
    onStatusChangeClick,
    profileOptions,
    activeProfileId,
    onProfileChangeClick,
    labels,
    shortcuts,
    onFreezeClick,
    onActivateClick,
    onDeleteClick,
  ]);

  return (
    <div data-testid={testId}>
      <Dropdown
        label={label}
        startIcon={<EllipsisHorizontalCircleIcon className="size-4" />}
        styleType="rounded"
        size="sm"
        disabled={disabled}
        className={cn('[&_span:nth-child(3)]:hidden', className)}
        listClassName="!z-[150]"
        menu={{
          items: menuItems,
          placement: 'bottom-start',
        }}
      />
    </div>
  );
}
